/**
 * sms-template — the read/write seam for ONE automatic message's SMS content.
 *
 * Extracted 2026-09-16 (CD docket "a separate editable SMS template for every
 * automatic message") when the short-lived standalone SMS screen was retired:
 * the operator confirmed the EXISTING Message Composer as the destination, so
 * the composer owns the UI and this module owns the data rules. Nothing here
 * renders.
 *
 * WHY NOT `getMessage(id)`. The docket names it, and it is the right call
 * against a finished backend — but `GET /v1/admin/messages/{id}` has no serving
 * binding in the design environment (501 `ROUTE_UNBOUND`), so it fails for every
 * row today. `findSmsRow` therefore resolves the row through the LIST op, which
 * IS bound and is the same read the switch grid runs — so the editor can never
 * disagree with the row the operator clicked. Pagination is exhausted before
 * giving up, because the route sorts globally and paginates after merging
 * lineages: page 1 is not the inventory. Swapping in `getMessage` once the
 * binding lands is a one-line change here and nowhere else.
 *
 * IDENTITY IS THE SERVER'S. The caller passes the id the list returned for the
 * SMS channel — never one built from a display name, a trigger, or by appending
 * a `:sms` suffix. Appointment identity includes the offset (a 60-minute
 * reminder is not the 24-hour one), and that is already carried by the row's own
 * id, so this module never has to reconstruct it.
 *
 * THE WRITE IS THE NARROWEST ONE THE CONTRACT ALLOWS: `{ perChannel: { sms } }`
 * and nothing else. Not the catalogue row echoed back, not `body` copied from
 * the email variant, and never `enabled` — editing text must not arm a disabled
 * message. The 1600-character ceiling is the contract's own
 * (`MESSAGE_BODY_MAX_CHARS.sms`), confirmed by the operator over the composer's
 * historical 320.
 */
import { listMessages, updateMessage } from "@v0y4ge/api-client";
import type { StudioMessage } from "@v0y4ge/api-contracts";
import { tokensFor, findTokens, type MsgContext } from "./tokens";

/** The contract's per-channel ceiling for SMS. */
export const SMS_MAX = 1600;

const PAGE_LIMIT = 100;
/** A stop so a paginating read can never spin forever on a bad `hasMore`. */
const MAX_PAGES = 50;

/** The SMS row with this id, read through the bound list op. `null` means the
 *  catalogue serves no such SMS record — a dead link or a row whose SMS variant
 *  the platform has not delivered yet, which the caller must show as an explicit
 *  unavailable state rather than falling back to the email row. */
export async function findSmsRow(id: string): Promise<StudioMessage | null> {
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await listMessages({ kind: "message", channel: "sms", lineage: "all", page, limit: PAGE_LIMIT });
    const hit = res.messages.find((m) => m.id === id);
    if (hit) return hit;
    if (!res.pagination.hasMore || res.messages.length === 0) return null;
  }
  return null;
}

/** The stored SMS text: the channel override, else the channel-agnostic body for
 *  a row that has never carried an SMS variant. */
export function smsTextOf(m: StudioMessage): string {
  const perChannel = (m as { perChannel?: { sms?: string } }).perChannel;
  if (perChannel && typeof perChannel.sms === "string") return perChannel.sms;
  return typeof m.body === "string" ? m.body : "";
}

/** What the row is attached to decides which tokens can resolve. Derived from
 *  the server's own derived group — never from the row's name. */
export function smsTokenContext(m: StudioMessage | null): MsgContext {
  const id = m && m.group ? m.group.id : null;
  return id === "appointments" ? "appointment"
    : id === "events" ? "event"
      : id === "queues" ? "queue" : "unknown";
}

/** Tokens in the text this message cannot resolve. Reported, never removed: the
 *  text is the operator's, and the dispatch lane renders an unresolved token as
 *  empty, so naming them is the only useful thing to do. */
export function outOfScopeTokens(text: string, ctx: MsgContext): string[] {
  const allowed = new Set(tokensFor(ctx).map((tk) => tk.id));
  const seen: string[] = [];
  for (const found of findTokens(text)) {
    if (!found.known) continue; // TokenField already reports unknown ids
    if (allowed.has(found.id) || seen.includes(found.id)) continue;
    seen.push(found.id);
  }
  return seen;
}

export class SmsNotPersistedError extends Error {
  code = "SMS_TEXT_NOT_PERSISTED";
  constructor() { super("The server accepted the change but did not store the SMS text."); }
}

/**
 * Save the addressed SMS content and return the server's row.
 *
 * THE RESPONSE IS THE RECEIPT, NOT THE 200. A backend that accepts the PATCH and
 * drops `perChannel.sms` answers 200 with the row unchanged, and a success toast
 * on that is a lie the operator only discovers after a reload — so the stored
 * value is compared with what was sent and a silent drop is thrown as
 * `SmsNotPersistedError`.
 */
export async function saveSmsText(id: string, text: string): Promise<StudioMessage> {
  const server = await updateMessage(id, { perChannel: { sms: text } });
  if (smsTextOf(server) !== text) throw new SmsNotPersistedError();
  return server;
}
